/**
 * Swamithra — the seven-category taxonomy.
 *
 * The acceptance criteria require exactly seven categories and name two of them
 * — Salary and TDS (US-01-006.10) — but never enumerate the full set. The seven
 * below are the working set; they are DECLARED IN ONE PLACE because four
 * separate criteria depend on them agreeing:
 *
 *   - the create-query and create-vetting dropdowns (US-00-002.1, US-00-004.1)
 *   - the Expert dashboard's Category filter (US-01-001.2)
 *   - the Expert's category correction, which must be honoured by that filter
 *     and by reporting (US-01-001.8)
 *   - the reporting bar chart, which renders ALL seven every time, with zero for
 *     the ones that have no queries in the window (US-01-006.9)
 *
 * Changing the set is a wire-contract change: the values are stored on every
 * query and vetting request, so a rename needs a migration, not an edit here.
 * These seven ARE the service's taxonomy, in its display order — a value it does
 * not recognise is refused with 422 on submission and on the dashboard's
 * Category filter, and a query tagged with one it sends and this list lacks
 * fails the parse of the whole list.
 */

import { z } from "zod"

export const queryCategorySchema = z.enum([
	"salary",
	"tds",
	"gst",
	"capital_gains",
	"business_income",
	"refund",
	"other",
])

export type QueryCategory = z.infer<typeof queryCategorySchema>

export const QUERY_CATEGORY_LABELS: Record<QueryCategory, string> = {
	salary: "Salary",
	tds: "TDS",
	gst: "GST",
	capital_gains: "Capital Gains",
	business_income: "Business Income",
	refund: "Refund",
	other: "Other",
}

/**
 * Dropdown and chart order — the service's, so the bar chart and the export
 * agree column for column (US-01-006.9). `other` stays last.
 */
export const QUERY_CATEGORIES: readonly QueryCategory[] = queryCategorySchema.options

export function queryCategoryLabel(category: QueryCategory): string {
	return QUERY_CATEGORY_LABELS[category]
}

/**
 * Options for a `<Select>` or a native picker, in one shape so web and mobile
 * cannot render different lists.
 */
export const QUERY_CATEGORY_OPTIONS: readonly { value: QueryCategory; label: string }[] =
	QUERY_CATEGORIES.map(value => ({ value, label: QUERY_CATEGORY_LABELS[value] }))

/**
 * A zero-filled tally over every category.
 *
 * The reporting chart must show all seven with zeros rather than only the ones
 * present in the response (US-01-006.9), and a chart component that has to
 * remember that is a chart component that will forget.
 */
export function emptyCategoryCounts(): Record<QueryCategory, number> {
	return Object.fromEntries(QUERY_CATEGORIES.map(c => [c, 0])) as Record<QueryCategory, number>
}
